import React from 'react';
import { isEqual, cloneDeep } from 'lodash';
import { Accordion } from 'react-bootstrap';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { plusOutlineIcon } from '@progress/kendo-svg-icons';

import {
  Algorithm,
  AlgorithmStep,
  AlgorithmControlStructure
} from '../../types/algorithm';
import {
  RemoveRoutesAsync,
  UpdateGameDocState
} from '../../utils/game-document';
import { StepEditor } from './step-editor';
import { StepSummary } from './summary-builder';
import { uuid } from '../../types/common-helper';
import { AlgorithmButtons } from './algorithm-buttons';
import { GameDocumentContext } from '../../contexts/game-document';
import { ControlStructureEditor } from './control-structure-editor';

export const LineWithComponentCentered: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  return (
    <div className={'d-flex align-items-center w-100'}>
      <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
      <div
        className={'d-flex align-items-center gap-2'}
        style={{ padding: '0 16px' }}>
        {children}
      </div>
      <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
    </div>
  );
};

export const AlgorithmEditor: React.FC<
  Algorithm & {
    taskId?: string;
    additionalClassname?: string;
    shortcutText: string;
  }
> = (props) => {
  const [state, setState] = React.useContext(GameDocumentContext);
  const [steps, setSteps] = React.useState<
    Array<AlgorithmStep | AlgorithmControlStructure>
  >(props.steps ?? []);
  const [initialIds] = React.useState(() => new Set(steps.map((s) => s.id)));

  const onChange = React.useCallback(
    (updatedSteps: Array<AlgorithmStep | AlgorithmControlStructure>) => {
      props.onChange?.(updatedSteps);
    },
    [props.onChange]
  );

  const addStep = (index: number) => {
    const newSteps = cloneDeep(steps);
    newSteps.splice(index, 0, { id: uuid() });
    setSteps(newSteps);
    onChange(newSteps);
  };

  const addControlStructure = (index: number) => {
    const newSteps = cloneDeep(steps);
    newSteps.splice(index, 0, {
      id: uuid(),
      condition: 'select',
      ifSteps: [
        {
          id: uuid()
        }
      ],
      elseSteps: [
        {
          id: uuid()
        }
      ]
    });
    setSteps(newSteps);
    onChange(newSteps);
  };

  const handleStepChange = (
    step: AlgorithmStep | AlgorithmControlStructure
  ) => {
    const newSteps = cloneDeep(steps);
    const index = newSteps.findIndex((s) => s.id === step.id);
    if (index !== -1) newSteps[index] = step;

    setSteps(newSteps);
    onChange(newSteps);
  };

  const onDeleteStep = (step: AlgorithmStep) => {
    if (step.routeId) {
      const route = (state.gameDocument?.assets.routes ?? []).find(
        (r) => r.id === step.routeId
      );
      if (route) {
        RemoveRoutesAsync(state?.gameDocument!, route).then((response) => {
          setState((prev) => UpdateGameDocState(prev, response));
        });
      }
    }

    const newSteps = steps.filter((s) => s.id !== step.id);
    setSteps(newSteps);
    onChange(newSteps);
  };

  const onDeleteStructureStep = (step: AlgorithmControlStructure) => {
    const newSteps = steps.filter((s) => s.id !== step.id);
    setSteps(newSteps);
    onChange(newSteps);
  };

  const stepMove = (
    arr: Array<AlgorithmStep | AlgorithmControlStructure>,
    from: number,
    to: number
  ) => {
    const newSteps = cloneDeep(arr);
    const item = newSteps[from];
    newSteps.splice(from, 1);
    newSteps.splice(to, 0, item);
    setSteps(newSteps);
    onChange(newSteps);
  };

  const handleMoveUp = (step: AlgorithmStep) => {
    const index = steps.findIndex((s) => s.id === step.id);
    if (index > 0) stepMove(steps, index, index - 1);
  };

  const handleMoveDown = (step: AlgorithmStep) => {
    const index = steps.findIndex((s) => s.id === step.id);
    if (index > -1 && index < steps.length - 1)
      stepMove(steps, index, index + 1);
  };

  const handleCopy = (step: AlgorithmStep) => {
    const newStep = cloneDeep(step);
    newStep.id = uuid();

    const newSteps = cloneDeep(steps);
    newSteps.push(newStep);

    setSteps(newSteps);
    onChange(newSteps);
  };

  const handleControlStructureCopy = (step: AlgorithmControlStructure) => {
    const newStep = cloneDeep(step);
    newStep.id = uuid();

    newStep.ifSteps?.forEach((x) => (x.id = uuid()));
    newStep.elseSteps?.forEach((x) => (x.id = uuid()));

    const newSteps = cloneDeep(steps);
    newSteps.push(newStep);

    setSteps(newSteps);
    onChange(newSteps);
  };

  const handleShortcutClick = (newStep: AlgorithmStep) => {
    const newSteps = cloneDeep(steps);
    newSteps.push(newStep);

    setSteps(newSteps);
    onChange(newSteps);
  };

  const startExpanded = (id: string) =>
    !initialIds.has(id) || initialIds.size === 1;

  const isTimerAction = props.action?.includes('Timer');

  React.useEffect(() => {
    if (!isEqual(props.steps, steps)) setSteps(steps ?? []);
  }, [steps]);

  return (
    <div className={`d-flex flex-column gap-4 ${props.additionalClassname}`}>
      <div
        className={`d-flex ${!isTimerAction ? 'flex-column align-items-center' : ''} gap-3`}>
        {!isTimerAction && (
          <span
            style={{
              fontSize: '24px',
              fontWeight: 'bold'
            }}>
            {props.shortcutText}
          </span>
        )}
        <div className={'d-flex gap-3'}>
          {[
            {
              text: 'Open a task',
              operation: 'openTask'
            },
            {
              text: 'Give an item',
              operation: 'giveItem'
            },
            {
              text: 'Give title',
              operation: 'giveTitle'
            },
            {
              text: 'Go to zone map',
              operation: 'navigateToZone'
            },
            {
              text: 'Other action',
              operation: 'other'
            }
          ].map((shortcut) => (
            <Button
              key={shortcut.operation}
              type={'button'}
              rounded={'full'}
              themeColor={
                shortcut.operation === 'other'
                  ? 'tertiary'
                  : isTimerAction
                    ? 'light'
                    : 'success'
              }
              {...(!isTimerAction && {
                fillMode: 'outline'
              })}
              onClick={() => {
                handleShortcutClick({
                  id: uuid(),
                  identifier: 'player',
                  action: props.action,
                  argumentAssetId: '',
                  argumentQuantity: 0,
                  operation:
                    shortcut.operation === 'other' ? '' : shortcut.operation
                });
              }}>
              {shortcut.text}
            </Button>
          ))}
        </div>
      </div>

      {steps.length > 0 && (
        <div className="algorithm-editor d-flex flex-column gap-4">
          {steps.map((step, idx) =>
            (step as AlgorithmControlStructure).condition ? (
              <ControlStructureEditor
                {...step}
                key={step.id}
                taskId={props.taskId}
                action={props.action}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                onDelete={onDeleteStructureStep}
                onCopy={handleControlStructureCopy}
                onChangeStructure={handleStepChange}
                defaultExpanded={startExpanded(step.id)}
              />
            ) : (
              <Accordion
                key={step.id}
                className={'algorithm-accordion'}
                defaultActiveKey={startExpanded(step.id) ? '0' : undefined}>
                <Accordion.Item eventKey={'0'}>
                  <Accordion.Header>
                    <div
                      className={
                        'algorithm-header d-flex align-items-center justify-content-between w-100 gap-3'
                      }>
                      <div
                        className={
                          'algorithm-header-left d-flex gap-3 align-items-center'
                        }>
                        <div
                          className={
                            'algorithm-label d-flex gap-1 align-items-center'
                          }
                          style={{ fontWeight: 'bold', fontSize: '24px' }}>
                          <span className={'material-symbols-outlined'}>
                            bolt
                          </span>
                          <span>Action</span>
                        </div>

                        <StepSummary step={step} />
                      </div>

                      <div
                        className={'algorithm-actions'}
                        onClick={(e) => e.stopPropagation()}>
                        <AlgorithmButtons
                          handleCopy={() => handleCopy(step)}
                          handleMoveUp={() => handleMoveUp(step)}
                          handleMoveDown={() => handleMoveDown(step)}
                          handleDeleteStep={() => onDeleteStep(step)}
                        />
                      </div>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <StepEditor
                      {...step}
                      idx={idx}
                      key={step.id}
                      taskId={props.taskId}
                      action={props.action}
                      onCopy={handleCopy}
                      onDelete={onDeleteStep}
                      onMoveUp={handleMoveUp}
                      onMoveDown={handleMoveDown}
                      onChange={handleStepChange}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            )
          )}
        </div>
      )}

      <LineWithComponentCentered>
        <Button
          themeColor={'dark'}
          style={{
            backgroundColor: '#043045',
            paddingLeft: '12px',
            paddingRight: '12px'
          }}
          className={'d-flex align-items-center'}
          onClick={() => addStep(steps.length + 1)}>
          <SvgIcon icon={plusOutlineIcon} className={'mr-2 text-primary'} />
          Add an action
        </Button>
        <Button
          fillMode={'outline'}
          themeColor={'dark'}
          style={{
            paddingLeft: '12px',
            paddingRight: '12px',
            backgroundColor: '#fff'
          }}
          className={'d-flex align-items-center'}
          onClick={() => addControlStructure(steps.length + 1)}>
          <SvgIcon icon={plusOutlineIcon} className={'mr-2 text-success'} />
          Add a rule
        </Button>
      </LineWithComponentCentered>
    </div>
  );
};
